'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useParams } from 'next/navigation';
import { useFormik } from 'formik';
import { debounce } from 'lodash';
import { Location } from '@/features/locations/types';
import { searchLocation } from '@/api/generated/locations';
import {
    filterDemoLocations,
    demoLocationsBySlugs,
} from '@/features/locations/fixtures/demoLocations';
import {
    ListingPropertySubtype,
    LISTING_PROPERTY_SUBTYPE_OPTIONS,
    ListingPropertyOriginalSubtype,
} from '../definitions/propertyTypes';
import { ListingsFilterSettings } from '../types';
import { ADVERT_TXN_TYPE_OPTIONS, AdvertTxnType } from '@/features/adverts/definitions/txnType';
import { AdvertTxnTypeOption } from '@/features/adverts/definitions/txnType';

export type ListingsClassicFilterFormValues = {
    locations: Location[];
    txnType: AdvertTxnTypeOption | null;
    propertyTypes: ListingPropertySubtype[];
    newBuildsOnly: boolean;
    withVideo: boolean;
    with3dTour: boolean;
};

export type ListingsClassicFilterState = ReturnType<typeof useListingsClassicFilter>;

export function useListingsClassicFilter(
    filterSettings: ListingsFilterSettings,
    searchParams: URLSearchParams,
) {
    const router = useRouter();
    const routeParams = useParams<{ txnType: string }>();
    const { minPrice, maxPrice, minArea, maxArea } = filterSettings;

    const [locationSuggestions, setLocationSuggestions] = useState<Location[]>([]);
    const [startedSearchLocations, setStartedSearchLocations] = useState(false);
    const [priceFrom, setPriceFrom] = useState(minPrice);
    const [priceTo, setPriceTo] = useState(maxPrice);
    const [changedPrice, setChangedPrice] = useState(false);
    const [areaFrom, setAreaFrom] = useState(minArea);
    const [areaTo, setAreaTo] = useState(maxArea);
    const [changedArea, setChangedArea] = useState(false);
    const searchParamsRef = useRef(new URLSearchParams());

    const formik = useFormik<ListingsClassicFilterFormValues>({
        initialValues: {
            locations: [],
            txnType: null,
            propertyTypes: [],
            newBuildsOnly: false,
            withVideo: false,
            with3dTour: false,
        },
        enableReinitialize: true,
        validate: (values) => {
            const errors: { locations?: string; txnType?: string } = {};
            if (!values.locations || values.locations.length === 0) {
                errors.locations = 'Vyberte aspoň 1 lokalitu.';
            }
            if (!values.txnType) {
                errors.txnType = 'Vyberte typ ponuky';
            }
            return errors;
        },
        onSubmit: () => {
            handleSearch();
        },
    });

    useEffect(() => {
        (async () => {
            searchParamsRef.current = searchParams;

            if (routeParams.txnType) {
                const found = ADVERT_TXN_TYPE_OPTIONS.find((i) => i.slug === routeParams.txnType);
                if (found) formik.setFieldValue('txnType', found);
            }

            const rawLocations = searchParams.get('locations');
            if (rawLocations) {
                const slugs = rawLocations
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean);
                const resolved = await searchLocationsBySlugs(slugs);
                formik.setFieldValue('locations', resolved);
            } else {
                formik.setFieldValue('locations', []);
            }

            const rawPropertyTypes = searchParams.get('propertyTypes');
            if (rawPropertyTypes) {
                const types = rawPropertyTypes
                    .split(',')
                    .map((v) => v.trim())
                    .map((v) => LISTING_PROPERTY_SUBTYPE_OPTIONS.find((t) => t.slug === v)?.key)
                    .filter(Boolean) as ListingPropertyOriginalSubtype[];
                formik.setFieldValue('propertyTypes', types);
            }

            formik.setFieldValue('newBuildsOnly', searchParams.get('newBuildsOnly') === '1');
            formik.setFieldValue('withVideo', searchParams.get('withVideo') === '1');
            formik.setFieldValue('with3dTour', searchParams.get('with3dTour') === '1');

            setPriceFrom(
                searchParams.get('priceFrom') && !isNaN(Number(searchParams.get('priceFrom')))
                    ? Number(searchParams.get('priceFrom'))
                    : minPrice,
            );
            setPriceTo(
                searchParams.get('priceTo') && !isNaN(Number(searchParams.get('priceTo')))
                    ? Number(searchParams.get('priceTo'))
                    : maxPrice,
            );
            setAreaFrom(
                searchParams.get('areaFrom') && !isNaN(Number(searchParams.get('areaFrom')))
                    ? Number(searchParams.get('areaFrom'))
                    : minArea,
            );
            setAreaTo(
                searchParams.get('areaTo') && !isNaN(Number(searchParams.get('areaTo')))
                    ? Number(searchParams.get('areaTo'))
                    : maxArea,
            );
        })();
    }, [routeParams, searchParams]);

    useEffect(() => {
        if (!changedPrice && (priceFrom !== minPrice || priceTo !== maxPrice)) {
            setChangedPrice(true);
        }
    }, [priceFrom, priceTo]);

    useEffect(() => {
        if (!changedArea && (areaFrom !== minArea || areaTo !== maxArea)) {
            setChangedArea(true);
        }
    }, [areaFrom, areaTo]);

    const displayLocations = useMemo(() => {
        if (!locationSuggestions.length) return [];
        const selectedSlugs = new Set(formik.values.locations.map((s) => s.slug));
        return locationSuggestions.filter((i) => !selectedSlugs.has(i.slug));
    }, [locationSuggestions, formik.values.locations]);

    const startSearchLocation = useCallback(
        debounce(async (value: string) => {
            if (value) {
                setStartedSearchLocations(true);
                try {
                    const results = await searchLocation({ search: value });
                    // API ma prednost; ak nie je dostupne (offline vrati napr. HTML
                    // string) alebo nic nevrati, fallback na seed - a Array.isArray
                    // chrani displayLocations.map pred padom.
                    const list =
                        Array.isArray(results) && results.length
                            ? results
                            : filterDemoLocations(value);
                    setLocationSuggestions(list);
                } catch {
                    setLocationSuggestions(filterDemoLocations(value));
                }
            } else {
                setLocationSuggestions([]);
            }
        }, 300),
        [],
    );

    async function searchLocationsBySlugs(slugs: string[]) {
        try {
            const results = await searchLocation({ slugs: slugs.join(',') });
            return Array.isArray(results) && results.length
                ? results
                : demoLocationsBySlugs(slugs);
        } catch {
            return demoLocationsBySlugs(slugs);
        }
    }

    function buildFilterQuery(p: {
        page?: number | null;
        locations?: Location[];
        propertyTypes?: ListingPropertySubtype[];
        priceFrom?: number;
        priceTo?: number;
        areaFrom?: number;
        areaTo?: number;
        newBuildsOnly?: boolean;
        withVideo?: boolean;
        with3dTour?: boolean;
    }) {
        const qs = new URLSearchParams();
        if (p.page) qs.set('page', p.page.toString());
        if (p.locations?.length) {
            qs.set('locations', p.locations.map((l) => l.slug).join(','));
        }
        if (p.propertyTypes?.length) {
            const slugs = p.propertyTypes
                .map((t) => LISTING_PROPERTY_SUBTYPE_OPTIONS.find((o) => o.key === t)?.slug)
                .filter(Boolean) as string[];
            qs.set('propertyTypes', slugs.join(','));
        }
        if (typeof p.priceFrom === 'number') qs.set('priceFrom', String(p.priceFrom));
        if (typeof p.priceTo === 'number') qs.set('priceTo', String(p.priceTo));
        if (typeof p.areaFrom === 'number') qs.set('areaFrom', String(p.areaFrom));
        if (typeof p.areaTo === 'number') qs.set('areaTo', String(p.areaTo));
        if (p.newBuildsOnly) qs.set('newBuildsOnly', '1');
        if (p.withVideo) qs.set('withVideo', '1');
        if (p.with3dTour) qs.set('with3dTour', '1');
        return qs.toString();
    }

    /** Filter v tvare, ktory ocakava GET /api/v1/listings (pozn. ine nazvy klucov
        nez buildFilterQuery - locationSlugs vs locations). Pouzity na zistenie
        poctu vysledkov. */
    function buildFilterParams() {
        const propertySlugs = formik.values.propertyTypes
            .map((t) => LISTING_PROPERTY_SUBTYPE_OPTIONS.find((o) => o.key === t)?.slug)
            .filter(Boolean) as string[];

        return {
            // API ocakava key (rovnako ako /inzeraty/[txnType]/page.tsx), nie slug
            ...(formik.values.txnType ? { txnType: formik.values.txnType.key } : {}),
            ...(formik.values.locations.length
                ? { locationSlugs: formik.values.locations.map((l) => l.slug).join(',') }
                : {}),
            ...(propertySlugs.length ? { propertyTypes: propertySlugs.join(',') } : {}),
            priceFrom,
            priceTo,
            areaFrom,
            areaTo,
            ...(formik.values.newBuildsOnly ? { newBuildsOnly: true } : {}),
            ...(formik.values.withVideo ? { withVideo: true } : {}),
            ...(formik.values.with3dTour ? { with3dTour: true } : {}),
        };
    }

    /** Je vobec co mazat? Riadi stav tlacidla "Vymazat filtre". */
    const hasActiveFilters =
        formik.values.locations.length > 0 ||
        formik.values.propertyTypes.length > 0 ||
        formik.values.txnType !== null ||
        formik.values.newBuildsOnly ||
        formik.values.withVideo ||
        formik.values.with3dTour ||
        priceFrom !== minPrice ||
        priceTo !== maxPrice ||
        areaFrom !== minArea ||
        areaTo !== maxArea;

    /** Jeden reset pre vsetky plochy - formik aj cena/plocha mimo formiku. */
    function resetFilters() {
        formik.resetForm();
        setPriceFrom(minPrice);
        setPriceTo(maxPrice);
        setAreaFrom(minArea);
        setAreaTo(maxArea);
        setChangedPrice(false);
        setChangedArea(false);
    }

    function handleSearch() {
        const query = buildFilterQuery({
            page: searchParams.get('page') ? Number(searchParams.get('page')) : null,
            locations: formik.values.locations,
            propertyTypes: formik.values.propertyTypes,
            priceFrom,
            priceTo,
            areaFrom,
            areaTo,
            newBuildsOnly: formik.values.newBuildsOnly,
            withVideo: formik.values.withVideo,
            with3dTour: formik.values.with3dTour,
        });
        router.push(`/inzeraty/${formik.values.txnType?.slug}/?filter=classic&${query}`);
    }

    return {
        formik,
        displayLocations,
        startedSearchLocations,
        startSearchLocation,
        priceFrom,
        setPriceFrom,
        priceTo,
        setPriceTo,
        changedPrice,
        areaFrom,
        setAreaFrom,
        areaTo,
        setAreaTo,
        changedArea,
        handleSearch,
        buildFilterQuery,
        buildFilterParams,
        hasActiveFilters,
        resetFilters,
    };
}
